import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BaseModal from "./components/modals/BaseModal";
import PipelineHeader from "./components/PipelineHeader";
import PipelineTabs from "./components/PipelineTabs";
import PipelineFilters from "./components/PipelineFilters";
import PipelineBoard from "./components/PipelineBoard";
import PipelineOverviewPanel from "./components/PipelineOverviewPanel";
import PipelineDetailsDrawer from "./components/PipelineDetailsDrawer";
import ContactWizardModal from "./components/contact_wizard/ContactWizardModal";
import ContractModal from "./components/contract/ContractModal";
import PerformanceModal from "./components/performance/PerformanceModal";
import NegotiationModal from "./components/negotiation/NegotiationModal";
import AddInfluencerSourceModal from "./components/invite/AddInfluencerSourceModal";
import InviteInfluencerModal from "./components/invite/InviteInfluencerModal";
import { PIPELINE_STAGE_IDS } from "./constants/pipelineStages";
import {
  useFilteredPipelineCards,
  usePipelineBoard,
} from "./hooks/usePipelineBoard";
import { useCompanyCampaignOptions } from "./hooks/useCompanyCampaignOptions";
import { getInfluencerContactInfo } from "../../../marketplace/services/marketplaceApi";
import { pipelineService } from "../../services/pipelineService";
import PipelineGuide from "./components/pipeline_guide/PipelineGuide";
import { usePipelineGuide } from "./hooks/usePipelineGuide";
// import PipelineInboxModal from "./components/chat/PipelineInboxModal";
// import { useInstagramInbox } from "./hooks/useInstagramInbox";

function PipelinePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(/** @type {'board' | 'overview'} */("board"));
  const [nicheFilter, setNicheFilter] = useState("All");
  const [contactOpen, setContactOpen] = useState(false);
  const [contactCardId, setContactCardId] = useState(/** @type {string | null} */(null));
  const [contactPreset, setContactPreset] = useState(
    /** @type {{ initialChannel?: string, initialEmailTemplateId?: string, initialStep?: number } | null} */(null)
  );
  const [contractOpen, setContractOpen] = useState(false);
  const [performanceOpen, setPerformanceOpen] = useState(false);
  const [negotiationOpen, setNegotiationOpen] = useState(false);
  const [negotiationCardId, setNegotiationCardId] = useState(
    /** @type {string | null} */(null)
  );
  const [contractCardId, setContractCardId] = useState(
    /** @type {string | null} */(null)
  );
  const [performanceCardId, setPerformanceCardId] = useState(
    /** @type {string | null} */(null)
  );
  const [missingEmailOpen, setMissingEmailOpen] = useState(false);
  const [addInfluencerOptionsOpen, setAddInfluencerOptionsOpen] = useState(false);
  const [inviteInfluencerOpen, setInviteInfluencerOpen] = useState(false);
  const [inviteInfluencerSubmitting, setInviteInfluencerSubmitting] = useState(false);
  // const [inboxOpen, setInboxOpen] = useState(false);

  const pipelineGuide = usePipelineGuide();

  const {
    campaigns,
    loading: campaignsLoading,
  } = useCompanyCampaignOptions();

  const {
    cards,
    selectedCampaignId,
    stages,
    moveCard,
    advanceCard,
    setSelectedCampaignId,
    setCards,
    selectedCardId,
    openCard,
    closeCard,
    activityByCardId,
    refreshActivity,
    addNote,
    reloadSelectedCampaign,
  } = usePipelineBoard();

  const activeCampaigns = useMemo(
    () => campaigns.filter((c) => c.status === "active"),
    [campaigns]
  );

  /** Active campaigns for the pipeline selector; includes current campaign if it is not active so the control stays valid. */
  const pipelineCampaignOptions = useMemo(() => {
    const list = [...activeCampaigns];
    if (
      selectedCampaignId &&
      !list.some((c) => c.id === selectedCampaignId)
    ) {
      const sel = campaigns.find((c) => c.id === selectedCampaignId);
      if (sel) {
        list.unshift({
          ...sel,
          name:
            sel.status === "active"
              ? sel.name
              : `${sel.name} (inactive)`,
        });
      }
    }
    return list;
  }, [activeCampaigns, campaigns, selectedCampaignId]);

  const selectedCampaignMeta = useMemo(
    () => campaigns.find((c) => c.id === selectedCampaignId) || null,
    [campaigns, selectedCampaignId],
  );

  const selectedName = selectedCampaignMeta?.name ?? "";
  const selectedBrandName = selectedCampaignMeta?.brandName ?? "Your brand";

  const selectedIndicativeRateInr = useMemo(() => {
    const c = selectedCampaignMeta?.commission;
    if (!c || typeof c !== "object") return 0;
    if (c.model === "fixed") return Number(c.amount || 0);
    return 0;
  }, [selectedCampaignMeta?.commission]);

  // const inbox = useInstagramInbox({ campaignId: selectedCampaignId || undefined });

  const filteredCards = useFilteredPipelineCards(
    cards,
    selectedCampaignId,
    nicheFilter
  );

  const onAddInfluencer = useCallback(() => {
    setAddInfluencerOptionsOpen(true);
  }, []);

  const onAddFromMarketplace = useCallback(() => {
    setAddInfluencerOptionsOpen(false);
    navigate("/marketplace");
  }, [navigate]);

  const onOpenInviteInfluencer = useCallback(() => {
    if (!selectedCampaignId) {
      toast.info("Please select a campaign before inviting an influencer.");
      return;
    }
    setAddInfluencerOptionsOpen(false);
    setInviteInfluencerOpen(true);
  }, [selectedCampaignId]);

  const onNewCampaign = useCallback(() => {
    navigate("/company/dashboard/campaigns/create");
  }, [navigate]);

  // const onOpenInbox = useCallback(() => {
  //   setInboxOpen(true);
  // }, []);

  const onMore = useCallback(
    (cardId) => {
      openCard(cardId);
    },
    [openCard]
  );

  const openContactForCard = useCallback(
    (cardId) => {
      const card = cards.find((c) => c.id === cardId);
      if (card?.stageId === PIPELINE_STAGE_IDS.CONTACTED) {
        setContactPreset({
          initialChannel: "email",
          initialEmailTemplateId: "follow_up_email",
          initialStep: 2,
        });
      } else {
        setContactPreset(null);
      }
      setContactCardId(cardId);
      setContactOpen(true);
    },
    [cards]
  );

  /** Left button on pipeline cards — label varies by stage (see STAGE_PRIMARY_ACTION). */
  const handleCardPrimaryAction = useCallback(
    (cardId) => {
      const card = cards.find((c) => c.id === cardId);
      if (!card) return;

      if (card.stageId === PIPELINE_STAGE_IDS.NEGOTIATING) {
        setNegotiationCardId(cardId);
        setNegotiationOpen(true);
        return;
      }

      if (card.stageId === PIPELINE_STAGE_IDS.CONTRACTED) {
        setContractCardId(cardId);
        setContractOpen(true);
        return;
      }

      if (card.stageId === PIPELINE_STAGE_IDS.CONFIRMED) {
        setPerformanceCardId(cardId);
        setPerformanceOpen(true);
        return;
      }

      openContactForCard(cardId);
    },
    [cards, openContactForCard]
  );

  const handleAdvanceCard = useCallback(
    (cardId) => {
      const card = cards.find((c) => c.id === cardId);
      if (card?.stageId === PIPELINE_STAGE_IDS.CONFIRMED) {
        toast.success("Influencer marked complete for this pipeline.");
        return;
      }
      advanceCard(cardId);
    },
    [cards, advanceCard]
  );

  const selectedCampaignName = useMemo(
    () => campaigns.find((c) => c.id === selectedCampaignId)?.name || "",
    [campaigns, selectedCampaignId]
  );

  const onInviteInfluencerSubmit = useCallback(
    async (payload) => {
      if (!selectedCampaignId) {
        throw new Error("Please select a campaign first.");
      }
      setInviteInfluencerSubmitting(true);
      try {
        const response = await pipelineService.inviteInfluencerManual({
          campaignId: selectedCampaignId,
          ...payload,
        });
        await reloadSelectedCampaign();
        setInviteInfluencerOpen(false);
        if (String(response?.message || "").toLowerCase().includes("already exists")) {
          toast.info("Influencer is already shortlisted for this campaign.");
          return;
        }
        toast.success(
          response?.meta?.marketplaceCreated
            ? "Influencer added to marketplace and shortlisted."
            : "Influencer shortlisted successfully."
        );
      } finally {
        setInviteInfluencerSubmitting(false);
      }
    },
    [reloadSelectedCampaign, selectedCampaignId]
  );

  const selectedCard = useMemo(() => {
    if (!selectedCardId) return null;
    return cards.find((c) => c.id === selectedCardId) ?? null;
  }, [cards, selectedCardId]);

  const contactCard = useMemo(() => {
    if (!contactCardId) return null;
    return cards.find((c) => c.id === contactCardId) ?? null;
  }, [cards, contactCardId]);

  const selectedActivities = useMemo(() => {
    if (!selectedCardId) return [];
    return activityByCardId[selectedCardId] ?? [];
  }, [activityByCardId, selectedCardId]);

  useEffect(() => {
    if (!selectedCardId) return;
    refreshActivity(selectedCardId);
  }, [refreshActivity, selectedCardId]);

  const onDrawerAddNote = useCallback(
    (text) => {
      if (!selectedCardId) return;
      addNote(selectedCardId, text);
    },
    [addNote, selectedCardId]
  );

  const onDrawerContact = useCallback(() => {
    if (!selectedCardId) return;
    const id = selectedCardId;
    closeCard();
    window.setTimeout(() => {
      openContactForCard(id);
    }, 0);
  }, [closeCard, openContactForCard, selectedCardId]);

  const onDrawerContract = useCallback(() => {
    if (!selectedCardId) return;
    const id = selectedCardId;
    closeCard();
    window.setTimeout(() => {
      setContractCardId(id);
      setContractOpen(true);
    }, 0);
  }, [closeCard, selectedCardId]);

  const onDrawerNegotiate = useCallback(() => {
    if (!selectedCardId) return;
    const id = selectedCardId;
    closeCard();
    window.setTimeout(() => {
      setNegotiationCardId(id);
      setNegotiationOpen(true);
    }, 0);
  }, [closeCard, selectedCardId]);

  const onDrawerPerformance = useCallback(() => {
    if (!selectedCardId) return;
    const id = selectedCardId;
    closeCard();
    window.setTimeout(() => {
      setPerformanceCardId(id);
      setPerformanceOpen(true);
    }, 0);
  }, [closeCard, selectedCardId]);

  const onDrawerRemoveFromCampaign = useCallback(() => {
    if (!selectedCardId) return;
    const current = cards.find((c) => c.id === selectedCardId);
    if (!current) return;
    if (current.stageId === PIPELINE_STAGE_IDS.REJECTED) {
      toast.info("This influencer is already in rejected stage.");
      return;
    }
    moveCard(selectedCardId, PIPELINE_STAGE_IDS.REJECTED);
    toast.success("Influencer moved to rejected stage.");
  }, [cards, moveCard, selectedCardId]);

  const onContactSend = useCallback(
    async ({ channel, scheduleId, followUpId, subject, advertisingId, templateId, body, confirmed }) => {
      if (!contactCardId) return false;
      const card = cards.find((c) => c.id === contactCardId);
      if (!card) return false;

      const channelLabel = channel === "email" ? "Email" : "Instagram DM";

      if (channel === "email") {
        try {
          const username = String(card.handle || "")
            .trim()
            .replace(/^@/, "");
          const contact = await getInfluencerContactInfo(username);
          const email = contact?.email ? String(contact.email).trim() : "";

          if (!email) {
            setMissingEmailOpen(true);
            return false;
          }

          await pipelineService.inviteByEmail({
            itemId: card.__pipelineItemId,
            email,
            advertisingId,
            templateId,
          });

          moveCard(contactCardId, PIPELINE_STAGE_IDS.CONTACTED);
          refreshActivity(contactCardId);
          toast.success("Invitation email sent.");
          return true;
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to send invitation email";
          toast.error(message);
          return false;
        }
      }

      if (channel === "instagram_dm") {
        try {
          if (confirmed) {
            moveCard(contactCardId, PIPELINE_STAGE_IDS.CONTACTED);
            refreshActivity(contactCardId);
            toast.success("Marked as Connected.");

            // // Log the outgoing message in the local inbox timeline.
            // // This does NOT send a DM via Meta API; it only persists our local record.
            // if (card.__pipelineItemId && body) {
            //   try {
            //     await inbox.logOutgoing({
            //       pipelineItemId: card.__pipelineItemId,
            //       text: String(body),
            //     });
            //   } catch {
            //     // non-blocking
            //   }
            // }
          }
          return true;
        } catch (error) {
          toast.error(error?.message || "Failed to open Instagram DM");
          return false;
        }
      }

      moveCard(contactCardId, PIPELINE_STAGE_IDS.CONTACTED);
      refreshActivity(contactCardId);
      toast.success(`${channelLabel} prepared.`);
      return true;
    },
    [cards, contactCardId, moveCard, refreshActivity]
  );

  return (
    <div className="bg-[#F4F2EF] h-full w-full">
      <div className="w-full bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-orange-50 py-4 px-2 md:px-4">
        <PipelineHeader
          campaigns={pipelineCampaignOptions}
          selectedCampaignId={selectedCampaignId}
          onCampaignChange={setSelectedCampaignId}
          onAddInfluencer={onAddInfluencer}
          onNewCampaign={onNewCampaign}
          // onOpenInbox={onOpenInbox}
          // inboxUnread={inbox.globalUnread}
          campaignsLoading={campaignsLoading}
        />

        <div className="mt-4">
          {/* Top divider touching navbar/sidebar */}
          <div className="-mx-4 sm:-mx-6 lg:-mx-8 h-px bg-[#E3DFD9]" />

          {/* Tabs in full-width bar */}
          <div className="-mx-2 md:-mx-4 border-b border-[#E3DFD9] bg-white">
            <div className="pt-4 px-2 md:px-4">
              <PipelineTabs active={tab} onChange={setTab} />
            </div>
          </div>

          {/* Filters + divider for Board tab */}
          {tab === "board" && (
            <div className="-mx-2 md:-mx-4 border-b border-[#E3DFD9] bg-white">
              <div className="px-2 md:px-4 py-3 space-y-4">
                <PipelineFilters
                  active={nicheFilter}
                  onChange={setNicheFilter}
                />
              </div>
            </div>
          )}

          {/* Main content: Board or Overview */}
          <div className="mt-4">
            {tab === "board" ? (
              <PipelineBoard
                stages={stages}
                cards={filteredCards}
                onAdvanceCard={handleAdvanceCard}
                onPrimaryAction={handleCardPrimaryAction}
                onMore={onMore}
                onOpenCard={openCard}
                onAddInfluencer={onAddInfluencer}
              />
            ) : (
              <PipelineOverviewPanel
                cards={filteredCards}
                campaignName={selectedName}
              />
            )}
          </div>
        </div>
      </div>

      <PipelineDetailsDrawer
        open={Boolean(selectedCard)}
        card={selectedCard}
        activities={selectedActivities}
        onClose={closeCard}
        onAddActivity={onDrawerAddNote}
        onContact={onDrawerContact}
        onOpenContract={onDrawerContract}
        onNegotiate={onDrawerNegotiate}
        onOpenPerformance={onDrawerPerformance}
        onRemoveFromCampaign={onDrawerRemoveFromCampaign}
      />

      <ContactWizardModal
        open={contactOpen}
        influencer={contactCard}
        pipelineItemId={contactCard?.__pipelineItemId ?? null}
        campaignId={selectedCampaignId}
        campaignName={selectedName}
        brandName={selectedBrandName}
        onClose={() => setContactOpen(false)}
        initialChannel={contactPreset?.initialChannel}
        initialEmailTemplateId={contactPreset?.initialEmailTemplateId}
        initialStep={contactPreset?.initialStep}
        onSend={onContactSend}
      />
{/* 
      <PipelineInboxModal
        open={inboxOpen}
        onClose={() => setInboxOpen(false)}
        inbox={inbox}
        activeCampaignName={selectedName || "All campaigns"}
      /> */}

      <BaseModal
        open={missingEmailOpen}
        title="Email not available"
        onClose={() => setMissingEmailOpen(false)}
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setMissingEmailOpen(false)}
              className="px-4 py-2 rounded-xl bg-orange-500 text-white md:text-sm text-xs font-semibold hover:bg-orange-600 transition-colors"
            >
              OK
            </button>
          </div>
        }
      >
        <div className="p-5">
          <div className="text-xs md:text-sm text-gray-700">
            This influencer hasn&apos;t provided an email yet. You can connect with this
            influencer using other methods (for example, Instagram DM).
          </div>
        </div>
      </BaseModal>

      <ContractModal
        open={contractOpen}
        influencer={contractCardId ? cards.find((c) => c.id === contractCardId) ?? null : null}
        campaignName={selectedName}
        brandName={selectedBrandName}
        onClose={() => {
          setContractOpen(false);
          setContractCardId(null);
        }}
        onAddActivity={() => {}}
        onUpdated={(updatedItem) => {
          if (!updatedItem?._id) return;
          setCards({
            cards: cards.map((c) =>
              c.__pipelineItemId === String(updatedItem._id)
                ? {
                    ...c,
                    stageId: updatedItem.stageId ? String(updatedItem.stageId) : c.stageId,
                    invitation: updatedItem.invitation ?? c.invitation ?? null,
                    contract: updatedItem.contract || c.contract || null,
                  }
                : c
            ),
            activityByCardId: { ...activityByCardId },
          });
        }}
      />

      <NegotiationModal
        open={negotiationOpen}
        indicativeRateInr={selectedIndicativeRateInr}
        influencer={
          negotiationCardId
            ? cards.find((c) => c.id === negotiationCardId) ?? null
            : null
        }
        onViewContract={() => {
          if (!negotiationCardId) return;
          setNegotiationOpen(false);
          setContractCardId(negotiationCardId);
          setNegotiationCardId(null);
          setContractOpen(true);
        }}
        onClose={() => {
          setNegotiationOpen(false);
          setNegotiationCardId(null);
        }}
        onAddActivity={() => {}}
        onUpdated={(updatedItem) => {
          // Keep board in sync with backend negotiation payload
          if (!updatedItem?._id) return;
          setCards({
            cards: cards.map((c) =>
              c.__pipelineItemId === String(updatedItem._id)
                ? {
                    ...c,
                    stageId: updatedItem.stageId ? String(updatedItem.stageId) : c.stageId,
                    invitation: updatedItem.invitation ?? c.invitation ?? null,
                    negotiation: updatedItem.negotiation || c.negotiation || null,
                    contract: updatedItem.contract || c.contract || null,
                  }
                : c
            ),
            activityByCardId: { ...activityByCardId },
          });
        }}
      />

      <PerformanceModal
        open={performanceOpen}
        influencer={
          performanceCardId
            ? cards.find((c) => c.id === performanceCardId) ?? null
            : null
        }
        onClose={() => {
          setPerformanceOpen(false);
          setPerformanceCardId(null);
        }}
        onAddActivity={() => {}}


      />
      <AddInfluencerSourceModal
        open={addInfluencerOptionsOpen}
        onClose={() => setAddInfluencerOptionsOpen(false)}
        onAddFromMarketplace={onAddFromMarketplace}
        onInviteInfluencer={onOpenInviteInfluencer}
      />
      <InviteInfluencerModal
        open={inviteInfluencerOpen}
        onClose={() => setInviteInfluencerOpen(false)}
        submitting={inviteInfluencerSubmitting}
        campaignName={selectedCampaignName}
        onSubmit={onInviteInfluencerSubmit}
      />
      {pipelineGuide.isActive && (
        <PipelineGuide
          stepIndex={pipelineGuide.stepIndex}
          onNext={pipelineGuide.nextStep}
          onClose={pipelineGuide.endGuide}
        />
      )}
    </div>
  );
}

export default PipelinePage;